import classNames from "classnames";
import React, { useCallback, useEffect, useState } from "react";
import { ReCaptcha } from "../../../Shared/Components/ReCaptcha/ReCaptcha";
import { ProvetConfirmationView } from "../components/steps/provetConfirmationView";
import { ProvetFormSteps } from "../components/steps/provetFormSteps";
import { ProvetImportantInformationStep } from "../components/steps/provetImportantInformationStep";
import { useProvetContext } from "../context";
import {
  useBookingCancellation,
  useBookingFormActions,
  useBookingModalActions,
  useBookingSteps,
  useBookingSubmission,
  useDelayedScroll,
  useModalScrollIndicator,
  useProvetFocusManagement,
  useProvetModals,
  useProvetScrollManagement,
  useProvetTracking
} from "../hooks";
import { ProvetBookingFlowActionType } from "../hooks/provetBookingFlowReducer";
import { useProvetPreselection } from "../hooks/useProvetPreselection";
import { FormSelectItem, ProvetDialogStep } from "../types";
import { isUnconfirmedBookingError } from "../utilities/apiClient";
import { ProvetFormHeader } from "./provetFormHeader";
import { ProvetFormModals } from "./provetFormModals";

interface ProvetFormProps {
  activeProvetDepartmentIds: number[];
  advancedTrackingEnabled: boolean;
  bookingInformation?: string;
  defaultLatitude: number;
  defaultLongitude: number;
  defaultSpecies?: FormSelectItem[];
  defaultZoom: number;
  emergencyClinicsUrl?: string;
  excludedProvetDepartmentIds: number[];
  mapboxAccessToken?: string;
  oldBookingDomain?: string;
  preamble?: string;
  preludeSdkKey?: string;
  reCaptchaSiteKey?: string;
  sessionId: string;
  title?: string;
  treatmentNotes?: string;
}

/**
 * Main orchestrator component for the Provet online booking form.
 *
 * This is the central component that coordinates the entire multi-step booking flow:
 *
 * **Booking Flow Steps:**
 * 1. Important Information - Pre-booking disclaimers and information
 * 2. Select Clinic - Search and choose veterinary clinic
 * 3. Select Treatment - Choose reason for visit
 * 4. Select Date & Time - Pick appointment slot
 * 5. Contact Details - Enter customer and pet information
 * 6. Confirmation - Review and submit booking
 * 7. Confirmed - Success screen with booking details
 *
 * **Key Responsibilities:**
 * - Orchestrates multiple custom hooks for different concerns
 * - Coordinates scroll behavior, focus management, and tracking
 * - Handles pre-selection of clinic/treatment from URL params
 * - Manages form submission and error handling
 * - Switches between form view and confirmation view
 *
 * **Hook Composition (Granular Hooks):**
 * - `useProvetModals` - Modal state (open/close/content)
 * - `useProvetTracking` - Analytics event tracking
 * - `useProvetScrollManagement` - Auto-scroll on step changes
 * - `useProvetFocusManagement` - Auto-focus first field per step
 * - `useBookingSteps` - Step navigation and transitions
 * - `useBookingFormActions` - Form data management
 * - `useBookingModalActions` - Modal interactions
 * - `useBookingSubmission` - Form submission and reset
 * - `useBookingCancellation` - Booking cancellation
 * - `useModalScrollIndicator` - Scroll indicators in modals
 * - `useProvetPreselection` - Handle pre-selected clinic/treatment
 *
 * **Component Structure:**
 * - `ProvetFormHeader` - Title and icon
 * - `ProvetImportantInformationStep` - Step 1
 * - `ProvetFormSteps` - Steps 2-5 (clinic, treatment, time, contact)
 * - `ProvetFormModals` - All 6 modals (summary, treatment, cancellation, etc.)
 * - `ProvetConfirmationView` - Step 6 (success page)
 *
 * @param activeProvetDepartmentIds - IDs of departments that are bookable
 * @param advancedTrackingEnabled - Enable tracking of all steps (vs. just start/end)
 * @param bookingInformation - Post-booking information to display
 * @param defaultLatitude - Default map center latitude
 * @param defaultLongitude - Default map center longitude
 * @param defaultSpecies - Pre-populated species list
 * @param defaultZoom - Default map zoom level
 * @param emergencyClinicsUrl - URL to emergency clinics page
 * @param excludedProvetDepartmentIds - IDs of departments to hide
 * @param mapboxAccessToken - Mapbox access token for clinic map
 * @param oldBookingDomain - Legacy booking system URL for fallback
 * @param reCaptchaSiteKey - ReCaptcha v3 site key for spam protection
 * @param preamble - Optional form preamble displayed below the title
 * @param sessionId - Unique session identifier for tracking
 * @param title - Optional form title displayed at top
 * @param treatmentNotes - Additional notes about treatments
 *
 * @example
 * ```tsx
 * <ProvetForm
 *   sessionId="abc123"
 *   title="Book an Appointment"
 *   advancedTrackingEnabled={true}
 *   defaultLatitude={59.3293}
 *   defaultLongitude={18.0686}
 *   defaultZoom={8}
 *   mapboxAccessToken="your-access-token"
 *   activeProvetDepartmentIds={[1, 2, 3]}
 *   excludedProvetDepartmentIds={[99]}
 * />
 * ```
 */
export const ProvetForm = ({
  activeProvetDepartmentIds,
  advancedTrackingEnabled,
  bookingInformation,
  defaultLatitude,
  defaultLongitude,
  defaultSpecies,
  defaultZoom,
  emergencyClinicsUrl,
  excludedProvetDepartmentIds,
  mapboxAccessToken,
  oldBookingDomain,
  preamble,
  preludeSdkKey,
  reCaptchaSiteKey,
  sessionId,
  title,
  treatmentNotes
}: ProvetFormProps) => {
  const provetContext = useProvetContext();

  // Modal state management
  const modals = useProvetModals();

  // Tracking state management
  const tracking = useProvetTracking({
    advancedTrackingEnabled,
    sessionId,
    selectedReason: provetContext.bookingFlowState.formData?.selectedReason
  });

  // Handle pre-selection of clinic/treatment from URL
  const {
    preselectedClinic,
    preselectedTreatment,
    isTreatmentPreselectionPending,
    clearPreselectedClinic
  } = useProvetPreselection({
    visitedClinicBookingProvetId: provetContext.visitedClinicBookingProvetId,
    visitedTreatmentProvetId: provetContext.visitedTreatmentProvetId,
    departments: provetContext.departments,
    reasons: provetContext.reasons,
    reasonGroups: provetContext.reasonGroups,
    reasonsLoading: provetContext.reasonsStatus === "loading",
    selectedDepartmentId: provetContext.bookingFlowState.formData?.selectedDepartment?.id,
    selectedReasonId: provetContext.bookingFlowState.formData?.selectedReason?.id,
    setVisitedClinicBookingProvetId: provetContext.setVisitedClinicBookingProvetId,
    setVisitedTreatmentProvetId: provetContext.setVisitedTreatmentProvetId
  });

  // Scroll management
  const scrollRefs = useProvetScrollManagement({
    sessionId,
    activeStep: provetContext.bookingFlowState.activeStep,
    // The preselected clinic is found once the lists have loaded. (A preselected treatment
    // only shows once a clinic has been chosen, when the page shouldn't scroll back up.)
    hasSyncedPreselections: !!preselectedClinic
  });

  // Focus management - auto-focus first input/element in each step
  useProvetFocusManagement({
    sessionId,
    activeStep: provetContext.bookingFlowState.activeStep
  });

  const [recaptchaRefreshTrigger, setRecaptchaRefreshTrigger] = useState(0);

  const triggerRecaptchaRefresh = useCallback(() => {
    setRecaptchaRefreshTrigger(prev => prev + 1);
  }, []);

  // Granular booking flow hooks (replacing deprecated useProvetBookingFlow)
  const stepsHandlers = useBookingSteps({
    bookingFlowState: provetContext.bookingFlowState,
    bookingFlowDispatch: provetContext.bookingFlowDispatch,
    tracking
  });

  const submissionHandlers = useBookingSubmission({
    bookingFlowState: provetContext.bookingFlowState,
    bookingFlowDispatch: provetContext.bookingFlowDispatch,
    tracking,
    sessionId,
    modals: { summary: modals.summary },
    updateBookingStep: stepsHandlers.updateBookingStep,
    setEditDialog: stepsHandlers.setEditDialog,
    onTokenRefreshNeeded: triggerRecaptchaRefresh
  });

  const formActionsHandlers = useBookingFormActions({
    bookingFlowState: provetContext.bookingFlowState,
    bookingFlowDispatch: provetContext.bookingFlowDispatch,
    tracking,
    setEditDialog: stepsHandlers.setEditDialog,
    updateBookingStep: stepsHandlers.updateBookingStep,
    modals: { summary: modals.summary },
    scrollRefs
  });

  // An unconfirmed booking may already exist, so its error stays and keeps Confirm disabled
  const { submissionError, clearSubmissionError } = submissionHandlers;
  const clearRetryableSubmissionError = useCallback(() => {
    if (!isUnconfirmedBookingError(submissionError)) {
      clearSubmissionError();
    }
  }, [submissionError, clearSubmissionError]);

  const modalHandlers = useBookingModalActions({
    bookingFlowState: provetContext.bookingFlowState,
    bookingFlowDispatch: provetContext.bookingFlowDispatch,
    modals: {
      treatment: modals.treatment,
      summary: modals.summary,
      confirmClose: modals.confirmClose
    },
    scrollRefs,
    setReason: formActionsHandlers.setReason,
    updateBookingStep: stepsHandlers.updateBookingStep,
    clearSubmissionError: clearRetryableSubmissionError
  });

  const cancellationHandlers = useBookingCancellation({
    bookingFlowState: provetContext.bookingFlowState,
    tracking,
    modals: {
      cancelBooking: modals.cancelBooking,
      cancelSuccess: modals.cancelSuccess,
      cancelFailure: modals.cancelFailure
    },
    resetBooking: submissionHandlers.resetBooking
  });

  // Use scroll indicator hooks for modals
  useModalScrollIndicator({
    isModalActive: modals.treatment.active,
    scrollIndicatorRef: scrollRefs.treatmentScrollIndicatorRef,
    modalContentRef: scrollRefs.treatmentModalContentRef
  });

  useModalScrollIndicator({
    isModalActive: modals.summary.active,
    scrollIndicatorRef: scrollRefs.summaryScrollIndicatorRef,
    modalContentRef: scrollRefs.summaryModalContentRef
  });

  useModalScrollIndicator({
    isModalActive: modals.cancelBooking.active,
    scrollIndicatorRef: scrollRefs.cancelScrollIndicatorRef,
    modalContentRef: scrollRefs.cancelModalContentRef
  });

  // Initialize form data sessionId
  useEffect(() => {
    // Only set sessionId if it's not already set or if it has changed
    if (
      !provetContext.bookingFlowState.formData?.sessionId ||
      provetContext.bookingFlowState.formData.sessionId !== sessionId
    ) {
      provetContext.bookingFlowDispatch({
        type: ProvetBookingFlowActionType.SetFormData,
        formData: {
          ...(provetContext.bookingFlowState.formData ?? {}),
          sessionId
        }
      });
    }
  }, [sessionId, provetContext]);

  const handleReCaptchaTokenChange = useCallback(
    (token: string) => {
      provetContext.bookingFlowDispatch({
        type: ProvetBookingFlowActionType.SetFormData,
        formData: {
          reCaptchaResponse: token
        }
      });
    },
    [provetContext.bookingFlowDispatch]
  );

  // Validation error handler - using the new hook-based API
  const { scrollToError } = useDelayedScroll({
    errorSelector: ".validation-error",
    offset: 180,
    behavior: "smooth",
    delay: 200
  });

  // Modal action handlers for ProvetFormModals
  // Esc or click outside the summary asks whether to discard. The summary doesn't offer this
  // while the booking is sent or when it may already exist.
  const confirmCloseModal = () => {
    if (modals.summary.active) {
      modals.confirmClose.open();
    }
  };

  // "Discard booking" (D5): clear the booking and start again from Step 1. Details saved with
  // "Remember me" stay on the device, and the phone number has to be verified again.
  const discardBooking = async () => {
    modals.confirmClose.close();
    modals.summary.close();
    await submissionHandlers.resetBooking();
  };

  const closeConfirmCloseModal = () => {
    modals.confirmClose.close();
  };

  // closeSummaryModal also clears the submission error
  const onTimeslotConflict = () => {
    modalHandlers.closeSummaryModal();
    stepsHandlers.setEditDialog(ProvetDialogStep.SelectDateAndTime);
  };

  // The phone verification expired before the booking was sent (403): go back to the contact
  // details step to verify the phone number again. The error is cleared last, so Confirm
  // stays disabled until the summary has closed.
  const onPhoneVerificationExpired = async () => {
    try {
      await stepsHandlers.restartPhoneVerification();
      modals.summary.close();
      modals.confirmClose.close();
    } catch (error) {
      console.error("Failed to restart the phone verification:", error);
    } finally {
      submissionHandlers.clearSubmissionError();
    }
  };

  const bookingFlowActions = {
    onReasonSelected: modalHandlers.onReasonSelected,
    closeReasonInstructionsModal: modalHandlers.closeReasonInstructionsModal,
    onFormSubmit: submissionHandlers.onFormSubmit,
    closeSummaryModal: modalHandlers.closeSummaryModal,
    confirmCloseModal,
    closeConfirmCloseModal,
    discardBooking,
    clearSubmissionError: submissionHandlers.clearSubmissionError,
    onTimeslotConflict,
    onPhoneVerificationExpired,
    confirmCancelBooking: cancellationHandlers.confirmCancelBooking,
    closeCancelBookingModal: cancellationHandlers.closeCancelBookingModal,
    isCancelling: cancellationHandlers.isCancelling,
    closeCancelSuccessModal: cancellationHandlers.closeCancelSuccessModal,
    closeCancelFailureModal: cancellationHandlers.closeCancelFailureModal,
    submissionError: submissionHandlers.submissionError
  };

  return (
    <div className="flex flex-1 flex-col bg-neutral-0 sm:shadow-soft-blur sm:p-2xl sm:rounded-tl-br-lg lg:rounded-tl-br-xl">
      {/* Form Steps (1-5) */}
      <div
        className={classNames("block w-full", {
          "hidden":
            provetContext.bookingFlowState.activeStep === ProvetDialogStep.Confirmed
        })}
      >
        <ProvetFormHeader title={title} preamble={preamble} />

        <form
          ref={scrollRefs.formRef}
          noValidate
          onSubmit={
            provetContext.bookingFlowState.activeStep === ProvetDialogStep.Confirmation
              ? submissionHandlers.onFormSubmit
              : e => e.preventDefault()
          }
          className="flex flex-col gap-y-sm"
        >
          {/* ReCaptcha v3 - hidden input with auto-refreshing token. The action must
              match BookingFormData.ReCaptchaAction on the server. */}
          {reCaptchaSiteKey ? (
            <ReCaptcha
              siteKey={reCaptchaSiteKey}
              action="provet_booking"
              onTokenRefreshed={handleReCaptchaTokenChange}
              refreshTrigger={recaptchaRefreshTrigger}
            />
          ) : (
            process.env.NODE_ENV === "development" &&
            console.warn(
              "ProvetForm: ReCaptcha site key not configured - spam protection disabled"
            )
          )}

          <ProvetImportantInformationStep emergencyClinicsUrl={emergencyClinicsUrl} />

          <ProvetFormSteps
            sessionId={sessionId}
            formResetCounter={provetContext.bookingFlowState.formResetCounter}
            treatmentNotes={treatmentNotes}
            defaultSpecies={defaultSpecies}
            oldBookingDomain={oldBookingDomain}
            mapboxAccessToken={mapboxAccessToken}
            preludeSdkKey={preludeSdkKey}
            activeProvetDepartmentIds={activeProvetDepartmentIds}
            excludedProvetDepartmentIds={excludedProvetDepartmentIds}
            defaultLatitude={defaultLatitude}
            defaultLongitude={defaultLongitude}
            defaultZoom={defaultZoom}
            onSelectDepartment={formActionsHandlers.setDepartment}
            onSelectReason={modalHandlers.openReasonInstructionsModal}
            onSelectAppointmentTime={formActionsHandlers.setAppointment}
            onContactDetailsChanged={formActionsHandlers.setContactDetails}
            onValidationError={scrollToError}
            onEditStep={stepsHandlers.setEditDialog}
            onContactDetailsSubStepChange={stepsHandlers.changeContactDetailsSubStep}
            preselectedClinic={preselectedClinic}
            preselectedTreatment={preselectedTreatment}
            isTreatmentPreselectionPending={isTreatmentPreselectionPending}
            clearPreselectedClinic={clearPreselectedClinic}
          />
        </form>
      </div>

      {/* Step 6 - Confirmation Page */}
      {provetContext.bookingFlowState.activeStep === ProvetDialogStep.Confirmed && (
        <ProvetConfirmationView
          ref={scrollRefs.confirmationViewRef}
          sessionId={sessionId}
          formData={provetContext.bookingFlowState.formData}
          selectedCareUnit={tracking.selectedCareUnit}
          bookingInformation={bookingInformation}
          bookingId={tracking.bookingId}
          onResetBooking={submissionHandlers.resetBooking}
          onCancelBooking={cancellationHandlers.cancelBooking}
        />
      )}

      {/* All Modals */}
      <ProvetFormModals
        modals={modals}
        scrollRefs={scrollRefs}
        bookingFlowActions={bookingFlowActions}
        formData={provetContext.bookingFlowState.formData}
        isSubmitting={provetContext.bookingFlowState.bookingSubmitted}
        loadingAvailableAppointmentTimes={
          provetContext.bookingFlowState.loadingAvailableAppointmentTimes
        }
        activeStep={provetContext.bookingFlowState.activeStep}
        showSpeciesIncompatibleWarning={
          provetContext.bookingFlowState.showSpeciesIncompatibleWarning
        }
      />
    </div>
  );
};

export default ProvetForm;
